import { Dialog, Portal } from '@ark-ui/react';
import { useCallback, useMemo } from 'react';
import type { FollowListType } from '~/app/(default-layout)/user/[userId]/_containers/follows-container/types';
import { Loading } from '~/app/_components/loading';
import type { UserOverview } from '~/common/types/user-overview';
import { ModalHeader } from '@app/(default-layout)/user/[userId]/_containers/follows-container/modal-header';
import { TabsContainer } from '@app/(default-layout)/user/[userId]/_containers/follows-container/tabs-container';
import { UserFollowItem } from '@app/(default-layout)/user/[userId]/_containers/follows-container/user-follow-item';
import { Paragraph, styled } from '@styled-system/jsx';
import { Box, VStack } from '@styled-system/jsx';

type Props = {
  isOpen: boolean;
  userList: UserOverview[];
  listType: FollowListType;
  paginationRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  isLoadingFirstPage: boolean;
  isEmpty?: boolean;
  error?: boolean;
  user: UserOverview;
  onClose: () => void;
  onChangeList: (type: FollowListType) => void;
};

export const FollowListModal = ({
  isOpen,
  paginationRef,
  onClose,
  userList,
  isLoading,
  isLoadingFirstPage,
  error,
  user,
  isEmpty,
  listType,
  onChangeList,
}: Props) => {
  const handleOnOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );
  const noResultsText = useMemo(() => {
    return `${
      listType === 'follower' ? 'フォロワー' : 'フォロー中'
    }は見つかりませんでした`;
  }, [listType]);

  return (
    <Dialog.Root unmountOnExit open={isOpen} onOpenChange={handleOnOpenChange}>
      <Portal>
        <Backdrop />
        <Positioner>
          <Content>
            <ModalHeader onClose={onClose} user={user} />
            {/* Tabs */}
            <VStack padding="20px">
              <TabsContainer followListType={listType} onClick={onChangeList} />
              {/* No results case */}
              {isEmpty || error ? (
                <Paragraph
                  fontWeight="bold"
                  color="accent.500"
                  padding="100px 20px"
                >
                  {noResultsText}
                </Paragraph>
              ) : (
                <VStack
                  width="100%"
                  height="calc(100dvh - 148px)"
                  overflowY="scroll"
                >
                  {userList.map((user) => (
                    <UserFollowItem key={user.id} user={user} />
                  ))}
                  <Box
                    ref={paginationRef}
                    lg={{ height: '20px', marginTop: '-25px' }}
                  />
                  {!isLoadingFirstPage && isLoading && <Loading />}
                </VStack>
              )}
            </VStack>
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(4px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'modal_backdrop',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: 'calc(100dvh - 48px)',
    },

    width: {
      base: '100dvw',
      lg: '600px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    borderRadius: {
      base: 0,
      lg: '8px',
    },
    height: '100%',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});
